/**
 * PastOrderItem - Individual order card in the past orders list
 */

import React from 'react';
import { CompletedOrder, OrderPayload } from './types';

interface PastOrderItemProps {
	completedOrder: CompletedOrder;
	onPrint: ( order: OrderPayload ) => void;
	onDelete: ( key: string ) => void;
	translations: {
		customer: string;
		date: string;
		total: string;
		print: string;
		delete: string;
		items: string;
		promotions: string;
		synced: string;
		pending: string;
	};
}

const formatDate = ( isoString: string ): string => {
	const date = new Date( isoString );
	return date.toLocaleDateString( 'es-ES', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	} );
};

const formatItemsSummary = (
	order: OrderPayload,
	translations: PastOrderItemProps[ 'translations' ]
): string => {
	const itemCount = order.cartItems.reduce(
		( sum, item ) => sum + item.quantity,
		0
	);
	const promoCount = order.promotions.length;
	const parts: string[] = [];

	if ( itemCount > 0 ) {
		parts.push( `${ itemCount } ${ translations.items }` );
	}
	if ( promoCount > 0 ) {
		parts.push( `${ promoCount } ${ translations.promotions }` );
	}

	return parts.length > 0 ? parts.join( ', ' ) : '-';
};

export const PastOrderItem: React.FC< PastOrderItemProps > = ( {
	completedOrder,
	onPrint,
	onDelete,
	translations,
} ) => {
	const { order, savedAt } = completedOrder;
	const customerName =
		`${ order.customerInfo.nombre } ${ order.customerInfo.apellidos }`.trim() ||
		'-';
	const customerEmail = order.customerInfo.email || '-';

	return (
		<div className="sprintry-past-order-item">
			<div className="sprintry-past-order-item__header">
				<div className="sprintry-past-order-item__customer">
					<div className="sprintry-past-order-item__name">
						{ customerName }
					</div>
					<div className="sprintry-past-order-item__email">
						{ customerEmail }
					</div>
				</div>
				<div className="sprintry-past-order-item__status-section">
					<span
						className={ `sprintry-past-order-item__status-badge ${
							completedOrder.synced !== false
								? 'sprintry-past-order-item__status-badge--synced'
								: 'sprintry-past-order-item__status-badge--pending'
						}` }
					>
						{ completedOrder.synced !== false ? (
							<>
								<svg
									width="12"
									height="12"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="3"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<polyline points="20 6 9 17 4 12" />
								</svg>
								{ translations.synced }
							</>
						) : (
							<>
								<svg
									width="12"
									height="12"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="3"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<circle cx="12" cy="12" r="10" />
									<polyline points="12 6 12 12 16 14" />
								</svg>
								{ translations.pending }
							</>
						) }
					</span>
					<div className="sprintry-past-order-item__total">
						{ order.totalAmount.toLocaleString( 'es-ES' ) }€
					</div>
				</div>
			</div>

			<div className="sprintry-past-order-item__details">
				<div className="sprintry-past-order-item__detail">
					<span className="sprintry-past-order-item__label">
						{ translations.date }:
					</span>
					<span className="sprintry-past-order-item__value">
						{ formatDate( savedAt ) }
					</span>
				</div>
				<div className="sprintry-past-order-item__detail">
					<span className="sprintry-past-order-item__label">
						{ translations.items }:
					</span>
					<span className="sprintry-past-order-item__value">
						{ formatItemsSummary( order, translations ) }
					</span>
				</div>
			</div>

			<div className="sprintry-past-order-item__actions">
				<button
					type="button"
					className="sprintry-past-order-item__print-btn"
					onClick={ () => onPrint( order ) }
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="6 9 6 2 18 2 18 9" />
						<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
						<rect x="6" y="14" width="12" height="8" />
					</svg>
					{ translations.print }
				</button>
				{ /* 				<button
					type="button"
					className="sprintry-past-order-item__delete-btn"
					onClick={() => onDelete(completedOrder.savedAt)}
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="3 6 5 6 21 6" />
						<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
						<line x1="10" y1="11" x2="10" y2="17" />
						<line x1="14" y1="11" x2="14" y2="17" />
					</svg>
					{translations.delete}
				</button> */ }
			</div>
		</div>
	);
};
